import * as React from 'react';
import {isNil} from 'lodash';
import * as SrcsetUtils from '@packages/systems/core/utils/SrcsetUtils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {handleCdnUrl} from '@packages/systems/core/utils/UrlUtils';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

import {
  withMeta,
  withSystem,
  Plugins,
  Atom,
  AttributesMap,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';

import {
  TText,
  TEnum,
  TRecord,
  TTypeReference,
  TTypeConstructor,
  TList,
  TNumber,
} from '@packages/systems/wfdl-ast';

import {SiteType} from '@packages/systems/core/records/Site';
import {
  type RenderContext,
  useAsset,
  useAssetFallback,
  useSite,
  useRuntimeEnvironment,
} from '@packages/systems/render';
import {
  isShallowBindingPathPojo,
  type BindingPathMapPojo,
} from '@packages/systems/dynamo/binding-types';
import {
  type EvaluatedNodeData,
  getArray,
  getBooleanOrElse,
  getInPojo,
  getRecord,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {
  getBindingPathFieldInnerTypePojo,
  getBindingPathFieldTypePojo,
} from '@packages/systems/dynamo/utils/BindingRecordUtils';
import {force} from '@packages/utilities/lazy';

const basicPlugin = Plugins.name('Basic');
const autoOrNumberType = TTypeConstructor(
  qualify(basicPlugin, typeName('AutoOrNumber'))
);

type ResponsiveAsset = Parameters<typeof SrcsetUtils.getSrcset>[0];
type ImageBindingData = {
  url?: string;
  file?: ResponsiveAsset;
  alt?: string | null;
};

const usesImageRefSetAlt = (
  legacyDynamoBindingPaths: BindingPathMapPojo | undefined
) => {
  const srcBind = legacyDynamoBindingPaths?.src;

  return Boolean(
    srcBind &&
    isShallowBindingPathPojo(srcBind) &&
    getBindingPathFieldTypePojo(srcBind) === 'Set' &&
    getBindingPathFieldInnerTypePojo(srcBind) === 'ImageRef'
  );
};

const imageSrc =
  (
    data: EvaluatedNodeData,
    hasBoundSrc: boolean,
    hasBoundImg: boolean,
    asset: RenderContext['assets'][string] | undefined,
    dynSrc: undefined | string,
    site: SiteType,
    runtimeEnvironment: Environment
  ) =>
  (src: unknown) => {
    if (hasBoundSrc) {
      const dynamicSrc = dynSrc || src;

      if (typeof dynamicSrc === 'string' && dynamicSrc.length > 0) {
        return handleCdnUrl(site, runtimeEnvironment)(dynamicSrc);
      } else {
        return '';
      }
    } else {
      const hotSelectPreviewSrc = getString(
        getInPojo(data, ['temp', 'img', 'src'])
      );

      // If our `img` data is bound or we have no `attr.src` set, get our `src`
      // from the asset; eventually we won't be relying on `attr.src` at all.
      //

      // - Tracking: https://github.com/webflow/webflow/issues/31085
      // - See also the legacy bound image src clearing behavior
      let assetSrc;

      if (
        (asset && asset.type === 'FIGMA') ||
        !src ||
        !hotSelectPreviewSrc ||
        hasBoundImg
      ) {
        // NOTE: We do not rely on `src` unless we cannot find a matching asset:
        if (asset) {
          assetSrc = asset.cdnUrl;
        } else {
          const imgUrl = getString(getInPojo(data, ['img', 'url']));

          if (imgUrl) {
            assetSrc = imgUrl;
          }
        }
      }

      const finalSrc = hotSelectPreviewSrc || assetSrc || src;

      if (typeof finalSrc === 'string' && finalSrc.length > 0) {
        return handleCdnUrl(site, runtimeEnvironment)(finalSrc);
      } else {
        return '';
      }
    }
  };

// If asset is bound then asset information is located under `data.img`
const getAssetFromImgData = (data: EvaluatedNodeData) => {
  const imageData = getRecord(getInPojo(data, ['img']));
  if (imageData) {
    const cdnUrl = getString(imageData.url);
    const file = getRecord(imageData.file);
    const asset = {cdnUrl, ...file} as ResponsiveAsset;
    return asset;
  }
};

const hasEvaluatedImagePayload = (data: EvaluatedNodeData) => {
  const imageData = getRecord(getInPojo(data, ['img']));
  return Boolean(
    imageData && (getString(imageData.url) || getRecord(imageData.file))
  );
};

const generateSrcsetAndSizes = (
  data: EvaluatedNodeData,
  asset: ResponsiveAsset,
  site: SiteType,
  runtimeEnvironment: Environment
) => {
  const srcSet = SrcsetUtils.getSrcset(
    asset,
    handleCdnUrl(site, runtimeEnvironment)
  );
  // TODO (Structure): https://webflow.atlassian.net/browse/STRUCT-352
  // Previously `sizes` was inside `data.img` where it has been now migrated to `data`.
  // This handles the case where `data.img` is a component property override default value that had `sizes` configured before the migration
  const sizes = getArray(
    getInPojo(data, ['img', 'sizes']) ?? getInPojo(data, ['sizes'])
  );
  const sizesString = SrcsetUtils.buildSizesString(sizes || null);
  return {sizes: sizesString, srcSet};
};

const responsiveImageData = (
  data: EvaluatedNodeData,
  image: ImageBindingData,
  site: SiteType,
  runtimeEnvironment: Environment
) => {
  const {url: cdnUrl, file} = image;
  const asset = {cdnUrl, ...file} as ResponsiveAsset;
  return generateSrcsetAndSizes(data, asset, site, runtimeEnvironment);
};

const withResponsiveImageAttributes =
  (
    data: EvaluatedNodeData,
    hasImgBinding: boolean,
    assetStoreAsset: RenderContext['assets'][string] | undefined,
    src: ImageBindingData | undefined,
    site: SiteType,
    runtimeEnvironment: Environment
  ) =>
  (attrs: AttributesMap) => {
    const hotSelectPreviewSrc = getString(
      getInPojo(data, ['temp', 'img', 'src'])
    );
    // TODO (Structure): https://webflow.atlassian.net/browse/STRUCT-352
    // Previously `srcsetDisabled` was inside `data.img` where it has been now migrated to `data`.
    // This handles the case where `data.img` is a component property override default value that had `srcsetDisabled` configured before the migration
    const isSrcsetDisabled = getBooleanOrElse(
      getInPojo(data, ['img', 'srcsetDisabled']) ??
        getInPojo(data, ['srcsetDisabled']),
      false
    );
    const imageDataAsset = getAssetFromImgData(data);
    const hasImagePayload = hasEvaluatedImagePayload(data);
    const responsiveImageAsset = assetStoreAsset ?? imageDataAsset;

    if (
      // For non-designer environnent, if Image node is bound, get the asset sizes and srcset directly from assets
      (hasImgBinding || hasImagePayload) &&
      site.get('responsiveImages') &&
      runtimeEnvironment !== WebflowEnvironments.DESIGNER
    ) {
      if (isSrcsetDisabled) {
        return attrs.delete('sizes').delete('srcset');
      }

      const {sizes, srcSet} = generateSrcsetAndSizes(
        data,
        responsiveImageAsset,
        site,
        runtimeEnvironment
      );

      return sizes && srcSet
        ? attrs.set('sizes', sizes).set('srcset', srcSet)
        : attrs.delete('sizes').delete('srcset');
    }

    if (
      hotSelectPreviewSrc ||
      isSrcsetDisabled ||
      // For now we must disable `srcset` if our image source is dynamically
      // bound, because we do not perform responsive measurements for symbol
      // instances with overrides.
      //
      // Tracking: https://github.com/webflow/webflow/issues/31087
      runtimeEnvironment === WebflowEnvironments.DESIGNER
    ) {
      return attrs.delete('sizes').delete('srcset');
    } else if (site.get('responsiveImages') && src && src.file) {
      const image = src;
      const {sizes, srcSet} = responsiveImageData(
        data,
        image,
        site,
        runtimeEnvironment
      );
      return sizes && srcSet
        ? attrs.set('sizes', sizes).set('srcset', srcSet)
        : attrs.delete('sizes').delete('srcset');
    } else {
      return attrs;
    }
  };

const Image = Atom.createRender(
  ({
    data,
    attributes,
    classes,
    generateAttributes,
    src,
    alt,
    legacyDynamoBindingPaths,
    fieldBindings: fieldBindingsLazy,
  }) => {
    const fieldBindings = force(fieldBindingsLazy);
    const nodeImgId = getStringOrElse(getInPojo(data, ['img', 'id']), '');
    const attrSrc = getInPojo(data, ['attr', 'src']);
    let nodeImgSrc = typeof attrSrc === 'string' ? attrSrc : '';
    const asset = useAsset(nodeImgId);
    const site = useSite();
    const hasBoundSrc = Boolean(legacyDynamoBindingPaths?.src);
    const hasBoundAlt = Boolean(legacyDynamoBindingPaths?.alt);

    // Whether the `img` field used a legacy dyn/search binding. `legacy` does not
    // mean that it is deprecated or unused. Every Collection List item and Search
    // result that binds an image runs through it, and legacyDynamoBindingPaths.img
    // is genuinely populated and consumed by those. These are different from other
    // Dynamo bindings (Dynamo.*, checked below with hasDynamoImgBinding).
    //
    // The same conceptual thing ("this image is CMS-driven") is stored in
    // structurally different locations depending on which mechanism created it:
    // - dyn/search → dedicated sub-structures: data.dyn.bind.* and data.search.bind.*.
    //   The binding descriptor lives beside the field, not in it. At eval time the
    //   dyn/search system resolves it against the current item and produces the
    //   evaluated data.img.
    // - Universal Binding / expression bindings (and overrides) → inline in the field's
    //   own data expression, e.g. data.img = Select(Dynamo, 'picture').
    const hasLegacyBoundImg = Boolean(legacyDynamoBindingPaths?.img);

    // Whether the `img` field itself is bound (any non-literal expression:
    // Universal Binding, component-property/symbol override, dyn/search, ...).
    // `legacyDynamoBindingPaths` only carries dyn/search bindings, so it cannot
    // see component/UB image overrides -- this is the faithful equivalent of the
    // legacy `Expressions.getIn(node.bind, imgBindingPath)` responsive check.
    const hasUniversalImgBinding = Boolean(
      fieldBindings?.img?.isUniversalBound
    );

    // Whether the `img` field is bound to a single dynamo (CMS) binding -- the
    // faithful equivalent of the legacy `isDynamoImgBinding(node.bind)` check.
    const hasDynamoImgBinding = Boolean(
      fieldBindings?.img?.dynamoUniversalBindingCount === 1
    );

    // nodeImgSrc can differ when SRR Rendering
    // (Specifically Rich Text Images)
    // So we need to update it to get the correct asset's alt text
    const runtimeEnvironment = useRuntimeEnvironment();
    if (runtimeEnvironment === WebflowEnvironments.SSR) {
      nodeImgSrc = imageSrc(
        data,
        hasBoundSrc,
        hasLegacyBoundImg,
        asset,
        src && src.url,
        site,
        runtimeEnvironment
      )(nodeImgSrc);
    }

    const assetFallback = useAssetFallback(asset, nodeImgSrc);
    const updatedAttributes = attributes
      .update((attrs) =>
        attrs.get('width') === 'auto' ? attrs.remove('width') : attrs
      )
      .update((attrs) =>
        attrs.get('height') === 'auto' ? attrs.remove('height') : attrs
      )
      .update((attrs) =>
        attrs.get('loading') === 'auto' ? attrs.remove('loading') : attrs
      )
      .update(
        'src',
        imageSrc(
          data,
          hasBoundSrc,
          hasLegacyBoundImg,
          asset,
          src && src.url,
          site,
          runtimeEnvironment
        )
      )
      .update('alt', (altText) => {
        if (usesImageRefSetAlt(legacyDynamoBindingPaths)) {
          return src && src.alt ? String(src.alt) : '';
        }
        if (hasBoundAlt) {
          return isNil(alt) ? '' : String(alt);
        }

        const shouldUseEvaluatedImageAlt =
          hasDynamoImgBinding || (!nodeImgId && hasEvaluatedImagePayload(data));
        const assetAlt =
          shouldUseEvaluatedImageAlt || !assetFallback
            ? getStringOrElse(getInPojo(data, ['img', 'alt']), '')
            : assetFallback.alt || '';
        if (altText === '__wf_reserved_decorative') {
          return '';
        } else if (isNil(altText) || altText === '__wf_reserved_inherit') {
          return assetAlt;
        } else {
          return altText;
        }
      })
      .update(
        withResponsiveImageAttributes(
          data,
          hasUniversalImgBinding,
          asset,
          src,
          site,
          runtimeEnvironment
        )
      );

    return (
      // TODO: fix jsx-a11y lint error
      // eslint-disable-next-line jsx-a11y/alt-text
      <img {...generateAttributes(updatedAttributes, classes)} />
    );
  }
);

const config = createDevlinkAtom({
  displayName: 'Image',
  icon: {type: 'Component', value: qualify(basicPlugin, iconName('Image'))},
  showEmpty: false,
  render: Image,

  fields: {
    img: withMeta(
      {type: 'image'},
      withSystem(
        {
          label: 'Image',
          isBindable: true,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Asset'), typeName('Image')))
      )
    ),
    temp: TRecord({
      img: TRecord({
        src: withSystem({private: true}, TText),
      }),
    }),
    srcsetDisabled: withSystem(
      {
        labelVariant: 'hidden',
        label: 'Disable responsiveness',
        defaultValue: false,
        isLocalizable: true,
      },
      TTypeConstructor(qualify(basicPlugin, typeName('ImageReversedBooleans')))
    ),
    attr: withSystem(
      {private: false},
      TRecord(
        {
          width: withSystem(
            {
              label: 'Width',
              defaultValue: 'auto',
              isLocalizable: true,
            },
            autoOrNumberType
          ),
          height: withSystem(
            {
              label: 'Height',
              defaultValue: 'auto',
              isLocalizable: true,
            },
            autoOrNumberType
          ),
          alt: withSystem(
            {
              label: 'Alt Text',
              isBindable: true,
              isLocalizable: true,
            },
            TTypeConstructor(qualify(basicPlugin, typeName('AltText')))
          ),
          src: withSystem(
            {
              label: 'Src',
              private: true,
              isLocalizable: true,
            },
            TText
          ),
          loading: withMeta(
            {
              variant: 'menu',
              withoutLivePreview: true,
            },
            withSystem(
              {
                label: 'Type',
                // New images have a default of `lazy` but the default here is still `auto` so that
                // existing images aren't affected (see https://github.com/webflow/webflow/pull/36883)
                defaultValue: 'auto',
                isLocalizable: true,
              },
              TEnum({
                lazy: 'Lazy: loads on scroll',
                eager: 'Eager: loads with page',
                auto: 'Auto: defaults to browser',
              })
            )
          ),
        },
        TTypeReference('extraAttrs')
      )
    ),
    sizes: withSystem(
      {
        label: 'sizes',
        private: true,
      },
      TList(
        TRecord({
          max: TNumber,
          size: TText,
        })
      )
    ),
  },
});

export default config;
